import React from 'react';
import { useIcsFormPdfPreview } from 'components/IAP/context/IcsFormPdfPreviewContext';
import { INCIDENT } from '../../ICSConstants';
import {
  IncidentComs,
  useIncidentComs,
  useCreateIncidentComs,
  useUpdateIncidentComs,
} from 'components/IAP/hooks/incidentComsHooks';
import { useSearchParams } from 'react-router';
import ICSWidget from './ICSWidget';
import { ConditionalCollapsibleCard } from 'components/IAP/ConditionalCollapsibleCard';
import { getIncidentCardTitle } from '../USCGPage';
import { useSelector } from 'react-redux';
import { selectGroupGuid } from 'slices/commonSelectors';
import IncidentComsTable, { EditableComsFields } from './IncidentComsTable';
import { SharedIcon } from 'components/SharedIcon/SharedIcon';
import { StylishNewToggleButtonGroup } from 'components/DesignSystems/New/StylishNewToggleButtonGroup';
import {
  useIncidentInfo,
  useUpdateIcsInfo,
} from 'components/IAP/hooks/incidentInfoHooks';
import StylishNewTextArea from 'components/DesignSystems/New/StylishNewTextArea';
import { useGetPositionsForIncident } from 'hooks/incident/useRoster';

const IncidentRadioCommunicationsPlan = () => {
  const [searchParams] = useSearchParams();
  const phase = searchParams.get('phase');
  const mode = searchParams.get('mode');
  const sectionKey = `${phase}-${mode}-incident-radio-communications-plan`;
  const { exportAsForm } = useIcsFormPdfPreview();
  const groupGuid = useSelector(selectGroupGuid);
  const [showArchived, setShowArchived] = React.useState(false);
  const [typeFilter, setTypeFilter] = React.useState<
    'all' | 'Radio' | 'Nonradio'
  >('all');
  const [specialInstructions, setSpecialInstructions] = React.useState('');

  const { data: communications, isLoading, error } = useIncidentComs(INCIDENT);
  const { data: roster } = useGetPositionsForIncident();
  const { data: incidentInfo } = useIncidentInfo();
  const updateIcsInfo = useUpdateIcsInfo();
  const createComsMutation = useCreateIncidentComs();
  const updateComsMutation = useUpdateIncidentComs();

  const isSaving = createComsMutation.isPending || updateComsMutation.isPending;

  const workGroups =
    roster
      ?.filter((member) => member.is_workgroup)
      .map((wg) => ({
        id: wg.id,
        name: wg.name,
      })) ?? [];

  React.useEffect(() => {
    setSpecialInstructions(
      incidentInfo?.communicationsSpecialInstructions ?? ''
    );
  }, [incidentInfo?.communicationsSpecialInstructions]);

  const handleExportICS205 = () => {
    void exportAsForm({ formname: 'ICS_205_CG', mapSketch: null });
  };

  const handleCreate = async (fields: EditableComsFields) => {
    await createComsMutation.mutateAsync({
      ...fields,
      type: fields.type ?? 'Radio',
      archived: false,
      group_guid: groupGuid,
      assignment: undefined,
      work_group_id: fields.work_group_id || undefined,
    });
  };

  const handleUpdate = async (
    row: IncidentComs,
    fields: EditableComsFields
  ) => {
    const { assignment: _assignment, ...rowWithoutAssignment } = row;
    await updateComsMutation.mutateAsync({
      comsId: row.id,
      data: {
        ...rowWithoutAssignment,
        ...fields,
        type: fields.type ?? row.type ?? 'Radio',
        assignment: undefined,
        work_group_id: fields.work_group_id || undefined,
      },
    });
  };

  const handleArchive = async (row: IncidentComs) => {
    const { assignment: _assignment, ...rowWithoutAssignment } = row;
    await updateComsMutation.mutateAsync({
      comsId: row.id,
      data: {
        ...rowWithoutAssignment,
        archived: !row.archived,
        assignment: undefined,
      },
    });
  };

  const activeCommunications =
    communications?.filter((comm) => !comm.archived) || [];
  const archivedCommunications =
    communications?.filter((comm) => comm.archived) || [];
  const byArchived = showArchived
    ? archivedCommunications
    : activeCommunications;
  const rowsToShow =
    typeFilter === 'all'
      ? byArchived
      : byArchived.filter(
          (comm) =>
            (comm.type ?? 'Radio').toLowerCase() === typeFilter.toLowerCase()
        );
  const notLoadingNoError = !isLoading && !error;

  return (
    <>
      <ConditionalCollapsibleCard
        sectionKey={sectionKey}
        className="planning-widget"
        contentClassName="content mt-2"
        headerLeft={
          <ICSWidget.HeaderLeft
            iconName="electric_bolt"
            iconColor="primary"
            title={getIncidentCardTitle('incidentRadioCommunicationsPlan')}
            subtitle="Radio frequency assignments and communication protocols"
          />
        }
        headerRight={
          <ICSWidget.HeaderRight
            icsRoles={['IC/UC', 'PSC', 'Situation Unit Leader']}
            handleExport={handleExportICS205}
            exportText="Preview ICS-205"
          />
        }
      >
        {isLoading && (
          <div className="text-center py-4">
            <div className="spinner-border" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        )}

        {error && (
          <div className="alert alert-danger">
            Error loading communication channels: {error.message}
          </div>
        )}

        {notLoadingNoError ? (
          <>
            <div className="d-flex align-items-center gap-2 text-muted small mb-2">
              <SharedIcon iconName="description" size="sm" />
              <span>ICS 205</span>
              <span>Incident Radio Communications Plan</span>
            </div>
            <div className="d-flex flex-wrap justify-content-end align-items-center gap-3 mb-2">
              <div className="d-flex align-items-center gap-2">
                <span className="small text-muted">View type:</span>
                <StylishNewToggleButtonGroup
                  data={[
                    { label: 'All', value: 'all' },
                    { label: 'Radio', value: 'Radio' },
                    { label: 'Nonradio', value: 'Nonradio' },
                  ]}
                  selected={typeFilter}
                  onChange={(value) =>
                    setTypeFilter(value as 'all' | 'Radio' | 'Nonradio')
                  }
                  size="sml"
                />
              </div>
              <div className="d-flex align-items-center gap-2">
                <span className="small text-muted">View:</span>
                <StylishNewToggleButtonGroup
                  data={[
                    { label: 'Active', value: 'active' },
                    { label: 'Archived', value: 'archived' },
                  ]}
                  selected={showArchived ? 'archived' : 'active'}
                  onChange={(value) => setShowArchived(value === 'archived')}
                  size="sml"
                />
              </div>
            </div>
            <IncidentComsTable
              rows={rowsToShow}
              workGroups={workGroups}
              onCreate={handleCreate}
              onUpdate={handleUpdate}
              onArchive={handleArchive}
              isSaving={isSaving}
            />
            <div className="mt-3">
              <label className="form-label small text-muted mb-1">
                Special Instructions:
              </label>
              <StylishNewTextArea
                className="ics205-cell-input"
                rows={3}
                placeholder="Enter a description..."
                value={specialInstructions}
                onChange={(e) => setSpecialInstructions(e.target.value)}
                onBlur={() => {
                  if (
                    incidentInfo &&
                    specialInstructions !==
                      (incidentInfo.communicationsSpecialInstructions ?? '')
                  ) {
                    updateIcsInfo.mutate({
                      ...incidentInfo,
                      communicationsSpecialInstructions: specialInstructions,
                    });
                  }
                }}
                disableLengthMessage
              />
            </div>
          </>
        ) : null}
      </ConditionalCollapsibleCard>
    </>
  );
};

export default IncidentRadioCommunicationsPlan;
